// @givmo/board — one line of the Form 990 page, opened (CBG-YR5-VIEW; frames p2, p3 and p9).
//
// Each block is something the board's records say, with a link to the record: a list of people or
// documents, line 8's small tables, a committee and your organization's answer, or the IRS's own
// sentences quoted with their source. Nothing here concludes: no check, no colour, no "missing".
//
// The tables follow Ramp's People table (the design's reference): plain words, an em dash for an
// empty cell, no badge. Line 8's tables, the board's two and each committee's, share one column
// grid, so the line-8 dates run down one column (the design's third by-eye round). A phone stacks
// each row: its first cell, then the others with their column's word.

import type { ReactNode } from 'react';
import { Popover, testIdProp } from '@givmo/design-system';
import { Link } from '../../board-routing';
import { BOARD_PAGES, CONFLICTS_TABS } from '../../board-routes';
import { displayDate } from '../../lib/display';
import { oneLine, periodLabel } from '../conflicts/coi-decode';
import {
  broadCommittees,
  committeesInOrder,
  compensationForLine,
  compensationPeople,
  cyclesForLine,
  line8Cell,
  minutesCell,
  type Form990Committee,
  type Form990CompensationDecision,
  type Form990Consent,
  type Form990Cycle,
  type Form990Evidence,
  type Form990Instructions,
  type Form990Line,
  type Form990Meeting,
  type Form990Policy,
  type Form990Quotation,
  type Form990ReturnCopy,
  type Form990ReturnReview,
  type Form990TaxYear,
  type Form990Version,
  type PolicyKind,
} from './form990-decode';
import { consentStatusLabel } from '../consents/consents-decode';
import { returnWords as rw } from '../returns/return-words';
import { form990Words as w } from './form990-words';

export const QUIET = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const PLAIN = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const STRONG = 'font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong';
const HEADING = 'font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary';
export const LINK =
  'font-w600 text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';
/** A committee's name: the row's own strong words, a link only by its hover (frame p2). */
const NAME_LINK =
  'font-w600 text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline';

// ── The IRS's words, quoted with their source (D-CBG-30) ─────────────────────────────────────────

/** One quotation: the IRS's sentence or paragraphs, verbatim, then where they are written. */
export function Quotation({
  paragraphs,
  citation,
  testId,
}: {
  paragraphs: readonly string[];
  citation: string;
  testId?: string;
}) {
  return (
    <figure
      className="flex flex-col gap-4 border-l-2 border-border-strong pl-12"
      {...testIdProp(testId)}
    >
      <blockquote className="flex flex-col gap-8">
        {paragraphs.map((paragraph, index) => (
          <p key={index} className={PLAIN}>
            {paragraph}
          </p>
        ))}
      </blockquote>
      <figcaption className={QUIET}>{citation}</figcaption>
    </figure>
  );
}

/**
 * "What the instructions say": the line's instruction excerpt in a popover (frame p9), verbatim, with
 * its citation. A long excerpt (line 1b's runs to ten paragraphs) scrolls inside the popover.
 */
export function WhatTheInstructionsSay({
  instructions,
  testId,
}: {
  instructions: Form990Instructions;
  testId: string;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger
        className="inline font-sans text-s13 font-w600 tracking-givmo text-brand-600 underline-offset-2 outline-none transition-colors hover:text-brand-700 hover:underline focus-visible:underline"
        {...testIdProp(testId)}
      >
        {w.whatInstructionsSay}
      </Popover.Trigger>
      <Popover.Content
        saas
        side="bottom"
        align="start"
        sideOffset={8}
        collisionPadding={16}
        aria-label={w.whatInstructionsSay}
        className="max-h-[min(70vh,520px)] w-[400px] max-w-[calc(100vw-32px)] overflow-y-auto p-16"
        testId={`${testId}-popover`}
      >
        <figure className="flex flex-col gap-10">
          <blockquote className="flex flex-col gap-10">
            {instructions.paragraphs.map((paragraph, index) => (
              <p
                key={index}
                className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary"
              >
                {paragraph}
              </p>
            ))}
          </blockquote>
          <figcaption className={QUIET}>{instructions.citation}</figcaption>
        </figure>
      </Popover.Content>
    </Popover.Root>
  );
}

// ── Small parts ──────────────────────────────────────────────────────────────────────────────────

/** A list of facts, one row each: what it is (often a link), then its quiet facts beside it. */
function FactRows({
  heading,
  rows,
  testId,
}: {
  heading?: string;
  rows: readonly { key: string; primary: ReactNode; secondary?: ReactNode }[];
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-6" {...testIdProp(testId)}>
      {heading !== undefined ? <span className={HEADING}>{heading}</span> : null}
      <ul className="flex flex-col">
        {rows.map((row) => (
          <li
            key={row.key}
            className="flex flex-col gap-x-16 gap-y-2 border-t border-border-subtle py-8 first:border-t-0 first:pt-0 sm:flex-row sm:items-baseline sm:justify-between"
          >
            <span className={STRONG}>{row.primary}</span>
            {row.secondary !== undefined ? (
              <span className={`${PLAIN} sm:text-right`}>{row.secondary}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A small table that stacks on a phone. Line 8's share the grid, so their dates align. A committee's
 * has no heading: the committee's own row heads it (frame p2).
 */
function SmallTable({
  heading,
  columns,
  rows,
  testId,
}: {
  heading?: string;
  columns: readonly string[];
  rows: readonly { key: string; cells: readonly ReactNode[] }[];
  testId: string;
}) {
  const widths = columns.length === 4 ? ['32%', '14%', '36%', '18%'] : ['32%', '50%', '18%'];
  return (
    <div className="flex flex-col gap-6" {...testIdProp(testId)}>
      {heading !== undefined ? <span className={HEADING}>{heading}</span> : null}
      <table className="hidden w-full table-fixed border-collapse sm:table">
        <colgroup>
          {widths.map((width, index) => (
            <col key={index} style={{ width }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                scope="col"
                className="border-b border-border-subtle pb-6 pr-16 text-left font-sans text-s12 font-w600 tracking-givmo text-text-tertiary"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="border-b border-border-subtle last:border-b-0">
              {row.cells.map((cell, index) => (
                <td
                  key={index}
                  className={`py-8 pr-16 align-top tabular-nums ${index === 0 ? STRONG : PLAIN}`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <ul className="flex flex-col sm:hidden">
        {rows.map((row) => (
          <li
            key={row.key}
            className="flex flex-col gap-2 border-t border-border-subtle py-8 first:border-t-0"
          >
            <span className={STRONG}>{row.cells[0]}</span>
            {row.cells.slice(1).map((cell, index) =>
              cell === null ? null : (
                <span key={index} className={PLAIN}>
                  <span className="text-text-tertiary">{columns[index + 1]}: </span>
                  {cell}
                </span>
              ),
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function versionFacts(version: Form990Version): ReactNode {
  return [
    version.adopted_on === null ? null : w.adoptedOn(displayDate(version.adopted_on)),
    version.imported ? w.imported : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

function VersionRows({
  heading,
  versions,
  testId,
}: {
  heading?: string;
  versions: readonly Form990Version[];
  testId: string;
}) {
  return (
    <FactRows
      heading={heading}
      testId={testId}
      rows={versions.map((version) => ({
        key: version.record_id,
        primary: (
          <span className="flex flex-col gap-2">
            <Link to="/records/$recordId" params={{ recordId: version.record_id }} className={LINK}>
              {version.title}
            </Link>
            {version.adopted_by !== null ? (
              <span className={QUIET}>
                {w.adoptedBy}{' '}
                <Link
                  to="/records/$recordId"
                  params={{ recordId: version.adopted_by.record_id }}
                  className={LINK}
                >
                  {version.adopted_by.title}
                </Link>
              </span>
            ) : null}
          </span>
        ),
        secondary: versionFacts(version),
      }))}
    />
  );
}

// ── Line 1a: the directors, and a broad committee's members ──────────────────────────────────────

function DirectorsEvidence({ evidence, testId }: { evidence: Form990Evidence; testId: string }) {
  const { seats } = evidence.directors;
  const committees = broadCommittees(evidence.committees);
  // Before Board held a seat, the organization's recorded term says it was a director (§4.3.1). Said
  // once when every seat is listed that way; beside each one when only some are.
  const allByTerm = seats.length > 0 && seats.every((seat) => seat.basis === 'term');
  return (
    <div className="flex flex-col gap-16" data-testid={testId}>
      {seats.length > 0 ? (
        <div className="flex flex-col gap-6">
          {allByTerm ? (
            <p className={QUIET} data-testid={`${testId}-by-terms`}>
              {w.fromRecordedTerms}
            </p>
          ) : null}
          <FactRows
            testId={`${testId}-seats`}
            rows={seats.map((seat) => ({
              key: seat.board_member_id,
              primary: (
                <Link
                  to="/roster/$memberId"
                  params={{ memberId: seat.board_member_id }}
                  className={LINK}
                >
                  {seat.display_name ?? '—'}
                </Link>
              ),
              secondary: [
                seat.title_labels.join(', '),
                seat.basis === 'term' && !allByTerm ? w.fromRecordedTerm : '',
              ]
                .filter((part) => part.length > 0)
                .join(' · '),
            }))}
          />
        </div>
      ) : null}
      {committees.map((committee, index) => (
        <div
          key={committee.committee_id}
          // A rule only between blocks: with no seat listed, the first committee opens the line.
          className={
            seats.length > 0 || index > 0
              ? 'flex flex-col gap-6 border-t border-border-subtle pt-12'
              : 'flex flex-col gap-6'
          }
          data-testid={`${testId}-committee-${committee.committee_id}`}
        >
          <p className={STRONG}>
            {w.committeeAnswered(committee.name, w.answerInSummary('broad'))}
          </p>
          <FactRows
            rows={(committee.members ?? []).map((member, index) => ({
              key: `${member.board_member_id}-${index}`,
              primary: (
                <Link
                  to="/roster/$memberId"
                  params={{ memberId: member.board_member_id }}
                  className={LINK}
                >
                  {member.display_name ?? '—'}
                </Link>
              ),
              secondary: [
                member.role === 'chair' ? w.roleChair : '',
                member.listed_as_director ? '' : w.notADirector,
                member.serves_to === null ? '' : w.until(displayDate(member.serves_to)),
              ]
                .filter((part) => part.length > 0)
                .join(' · '),
            }))}
          />
        </div>
      ))}
    </div>
  );
}

// ── Lines 1b, 2 and 12b: the annual disclosures ──────────────────────────────────────────────────

function cycleFacts(cycle: Form990Cycle): string {
  const opened = cycle.opened_at === null ? null : w.openedOn(displayDate(cycle.opened_at));
  const closed = cycle.closed_at === null ? null : w.closedOn(displayDate(cycle.closed_at));
  return [closed ?? opened, w.submitted(cycle.submitted_count, cycle.people_count)]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

function CyclesEvidence({
  cycles,
  line,
  testId,
}: {
  cycles: readonly Form990Cycle[];
  line: string;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-12" data-testid={testId}>
      <FactRows
        rows={cycles.map((cycle) => ({
          key: cycle.cycle_id,
          primary: (
            <span className="flex flex-col gap-2">
              <Link
                to={BOARD_PAGES.conflicts}
                search={{ tab: CONFLICTS_TABS.annual }}
                className={LINK}
              >
                {w.cycleTitle(cycle.label)}
              </Link>
              <span className={QUIET}>
                {w.askedAbout(periodLabel(cycle.period_start, cycle.period_end))}
              </span>
            </span>
          ),
          secondary: (
            <span className="flex flex-col gap-2 sm:items-end">
              <span>{cycleFacts(cycle)}</span>
              {cycle.close_record_id !== null ? (
                <Link
                  to="/records/$recordId"
                  params={{ recordId: cycle.close_record_id }}
                  className={LINK}
                >
                  {w.closeRecord}
                </Link>
              ) : null}
            </span>
          ),
        }))}
      />
      {line === '12b' ? null : <p className={QUIET}>{w.eachAnswered}</p>}
    </div>
  );
}

// ── Line 8's tables: a body's meetings and written consents, each with its line-8 date ───────────

/** A body's meetings held in the year: the board's on line 8a, a committee's on 8b (frame p2). */
function MeetingsTable({
  heading,
  meetings,
  body,
  testId,
}: {
  heading?: string;
  meetings: readonly Form990Meeting[];
  body: 'board' | 'committee';
  testId: string;
}) {
  return (
    <SmallTable
      heading={heading}
      testId={testId}
      columns={[w.colMeeting, w.colHeld, w.colMinutes, w.colLine8]}
      rows={meetings.map((meeting) => ({
        key: meeting.meeting_id,
        cells: [
          <Link
            key="title"
            to="/meetings/$meetingId/minutes"
            params={{ meetingId: meeting.meeting_id }}
            className={LINK}
          >
            {meeting.title}
          </Link>,
          displayDate(meeting.held_on),
          minutesCell(meeting.minutes, body),
          line8Cell(meeting.line_8_on, meeting.next_meeting_on),
        ],
      }))}
    />
  );
}

/** A body's written consents dated in the year, under the date each bears (CBG-FIX88). */
function ConsentsTable({
  heading,
  consents,
  testId,
}: {
  heading?: string;
  consents: readonly Form990Consent[];
  testId: string;
}) {
  return (
    <SmallTable
      heading={heading}
      testId={testId}
      columns={[w.colConsent, w.colDated, w.colLine8]}
      rows={consents.map((consent) => ({
        key: consent.consent_id,
        cells: [
          <Link
            key="title"
            to="/consents/$consentId"
            params={{ consentId: consent.consent_id }}
            className={LINK}
          >
            {consent.title ?? w.untitledConsent}
          </Link>,
          displayDate(consent.executed_on),
          line8Cell(consent.line_8_on, consent.next_meeting_on),
        ],
      }))}
    />
  );
}

// ── Line 8a: the board's meetings and written consents (frame p2) ────────────────────────────────

function MeetingsEvidence({ evidence, testId }: { evidence: Form990Evidence; testId: string }) {
  const { meetings, written_consents: consents, rule } = evidence.board_meetings;
  return (
    <div className="flex flex-col gap-16" data-testid={testId}>
      {meetings.length > 0 ? (
        <MeetingsTable
          heading={w.meetingsHeading}
          meetings={meetings}
          body="board"
          testId={`${testId}-meetings`}
        />
      ) : null}
      {consents.length > 0 ? (
        <ConsentsTable
          heading={w.consentsHeading}
          consents={consents}
          testId={`${testId}-consents`}
        />
      ) : null}
      {/* The clock's shipped sentence, verbatim (`board_contemporaneity.LINE_8_RULE_STATEMENT`). */}
      {rule.length > 0 ? (
        <p className={QUIET} data-testid={`${testId}-rule`}>
          {rule}
        </p>
      ) : null}
    </div>
  );
}

// ── Line 8b: each committee, your organization's answer, and its meetings (frames p2, p3) ────────

/**
 * One committee: its name and your organization's answer, then, for a committee answered "Yes" (the
 * only ones the read serves meetings and consents for, CBG-YR6-MEET), its own tables, each line-8
 * date counted against its own next meeting, or one line saying Board holds no record of them in
 * the year. `rule` says the committee's rule beneath them.
 */
function CommitteeRow({ committee, rule }: { committee: Form990Committee; rule: boolean }) {
  const [current, ...earlier] = committee.authority_answers;
  const meetings = committee.meetings ?? [];
  const consents = committee.written_consents ?? [];
  const served = committee.meetings !== null || committee.written_consents !== null;
  const testId = `form990-committee-${committee.committee_id}`;
  return (
    <li
      className="flex flex-col gap-4 border-t border-border-subtle py-10 first:border-t-0 first:pt-0"
      data-testid={testId}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between sm:gap-16">
        <span className={STRONG}>
          <Link
            to="/committees/$committeeId"
            params={{ committeeId: committee.committee_id }}
            className={NAME_LINK}
          >
            {committee.name}
          </Link>
          {committee.status === 'dissolved' && committee.dissolved_on !== null ? (
            <span className="font-w400 text-text-tertiary">
              {' · '}
              {/* The day kept whole: a phone wraps before it, never inside it. */}
              {w.dissolvedOn(oneLine(displayDate(committee.dissolved_on)))}
            </span>
          ) : null}
        </span>
        <span className={`${PLAIN} sm:text-right`}>
          {current === undefined ? (
            <span className="text-text-tertiary">—</span>
          ) : (
            <>
              {w.answerLabel(current.choice_value)}
              <span className="text-text-tertiary"> · {current.citation}</span>
            </>
          )}
        </span>
      </div>
      {earlier.map((answer, index) => (
        <span key={index} className={`${QUIET} sm:text-right`}>
          {w.earlierAnswer(
            w.answerLabel(answer.choice_value),
            answer.citation,
            answer.recorded_at === null ? null : displayDate(answer.recorded_at),
          )}
        </span>
      ))}
      {served ? (
        <div className="flex flex-col gap-16 pt-4">
          {meetings.length > 0 ? (
            <MeetingsTable meetings={meetings} body="committee" testId={`${testId}-meetings`} />
          ) : null}
          {consents.length > 0 ? (
            <ConsentsTable consents={consents} testId={`${testId}-consents`} />
          ) : null}
          {meetings.length + consents.length === 0 ? (
            <p className={QUIET} data-testid={`${testId}-none`}>
              {w.committeeNoneInYear}
            </p>
          ) : null}
          {rule ? (
            <p className={QUIET} data-testid="form990-committee-rule">
              {w.committeeRule}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function CommitteesEvidence({
  committees,
  quotations,
  scheduleO,
  testId,
}: {
  committees: readonly Form990Committee[];
  quotations: readonly Form990Quotation[];
  /** Line 8b's Schedule O draft, by the year's address; `null` on any other line. */
  scheduleO: Record<string, string | number> | null;
  testId: string;
}) {
  if (committees.length === 0) {
    // The no-committee edge (frame p3): the IRS's two sentences, each quoted with its source, and
    // nothing in Board's voice beside them but the way to line 8b's Schedule O draft (CBG-YR5-SCHO).
    return (
      <div className="flex flex-col gap-16" data-testid={`${testId}-no-committees`}>
        {quotations.map((quotation, index) => (
          <Quotation
            key={index}
            paragraphs={[quotation.text]}
            citation={quotation.citation}
            testId={`${testId}-quotation-${index}`}
          />
        ))}
        {scheduleO !== null ? (
          <Link
            to={BOARD_PAGES.scheduleO}
            search={scheduleO}
            className={`font-sans text-s13 ${LINK}`}
            data-testid={`${testId}-schedule-o`}
          >
            {w.scheduleODraftFor8b}
          </Link>
        ) : null}
      </div>
    );
  }
  const ordered = committeesInOrder(committees);
  // The committee's rule is one sentence for every committee, so it is said once, under the last
  // committee whose tables show a line-8 date (frame p2 draws one such committee).
  const ruleAt = ordered.findLastIndex(
    (committee) =>
      (committee.meetings?.length ?? 0) + (committee.written_consents?.length ?? 0) > 0,
  );
  return (
    <ul className="flex flex-col" data-testid={testId}>
      {ordered.map((committee, index) => (
        <CommitteeRow key={committee.committee_id} committee={committee} rule={index === ruleAt} />
      ))}
    </ul>
  );
}

// ── Lines 12a, 13 and 14: a policy in force at the year's end, and the year's versions ───────────

function PolicyEvidence({
  policy,
  taxYear,
  testId,
}: {
  policy: Form990Policy;
  taxYear: Form990TaxYear;
  testId: string;
}) {
  const inForce = policy.in_force;
  const others = policy.adopted_in_year.filter(
    (version) => inForce === null || version.record_id !== inForce.record_id,
  );
  return (
    <div className="flex flex-col gap-16" data-testid={testId}>
      {inForce !== null ? (
        <VersionRows
          // A year not ended reads the versions adopted so far (contract C11): its last day is still
          // ahead, so the heading names no day.
          heading={taxYear.ended ? w.inForceOn(displayDate(taxYear.ends_on)) : w.inForce}
          versions={[inForce]}
          testId={`${testId}-in-force`}
        />
      ) : null}
      {others.length > 0 ? (
        <VersionRows heading={w.adoptedInYear} versions={others} testId={`${testId}-adopted`} />
      ) : null}
    </div>
  );
}

// ── Line 12c: the register, and the meetings whose record holds a recusal ────────────────────────

function ConflictsEvidence({ evidence, testId }: { evidence: Form990Evidence; testId: string }) {
  return (
    <div className="flex flex-col gap-12" data-testid={testId}>
      <p className={PLAIN}>
        <Link to={BOARD_PAGES.conflicts} className={LINK}>
          {w.registerLink}
        </Link>
      </p>
      {evidence.conflicts.length > 0 ? (
        <FactRows
          rows={evidence.conflicts.map((meeting) => ({
            key: meeting.meeting_id,
            primary: (
              <span className="flex flex-col gap-2">
                <Link
                  to="/meetings/$meetingId/record"
                  params={{ meetingId: meeting.meeting_id }}
                  className={LINK}
                >
                  {meeting.title}
                </Link>
                <span className={QUIET}>{displayDate(meeting.held_on)}</span>
              </span>
            ),
            secondary: [
              meeting.recusal_recorded ? w.recusalRecorded : '',
              meeting.determination_recorded ? w.determinationRecorded : '',
            ]
              .filter((part) => part.length > 0)
              .join(' · '),
          }))}
        />
      ) : null}
    </div>
  );
}

// ── One line's evidence, by the kinds the read names for it ──────────────────────────────────────

const POLICY_KINDS: readonly PolicyKind[] = [
  'coi_policy',
  'whistleblower_policy',
  'document_retention_policy',
];

// ── Line 11a: the copies of the return provided to the board (CBG-YR5-VIEW-11A) ───────────────────

/** Each copy of this tax year's return, newest first, with its receipts' counts and the way to them. */
function ReturnCopiesEvidence({
  copies,
  testId,
}: {
  copies: readonly Form990ReturnCopy[];
  testId: string;
}) {
  return (
    <FactRows
      heading={rw.copiesHeading}
      testId={testId}
      rows={copies.map((copy) => ({
        key: copy.record_id,
        primary: (
          <span className="flex flex-col gap-2">
            <Link
              to="/form-990/returns/$recordId"
              params={{ recordId: copy.record_id }}
              className={LINK}
              data-testid={`${testId}-copy-${copy.record_id}`}
            >
              {rw.copyProvidedOn(displayDate(copy.provided_on), copy.provided_by_display_name)}
            </Link>
          </span>
        ),
        secondary: [
          countsOf(copy),
          copy.withdrawn_at !== null ? rw.copyWithdrawn : null,
          copy.withdrawn_at === null && copy.followed_by_record_id !== null
            ? rw.copyFollowed
            : null,
        ]
          .filter((part): part is string => part !== null)
          .join(' · '),
      }))}
    />
  );
}

function countsOf(copy: Form990ReturnCopy): string {
  return rw.receiptCounts(
    copy.opened_count,
    copy.printed_copy_count,
    copy.read_recorded_count,
    copy.recipient_count,
  );
}

// ── Line 11b: the board's review, linked (CBG-YR5-REVIEW) ─────────────────────────────────────────

/** Each copy's linked review, newest copy first: the meeting by its own title and day, or the consent. */
function ReturnReviewEvidence({
  reviews,
  testId,
}: {
  reviews: readonly Form990ReturnReview[];
  testId: string;
}) {
  return (
    <FactRows
      heading={rw.theBoardsReview}
      testId={testId}
      rows={reviews.map((review) => {
        const motion = review.motion;
        const consent = review.written_consent;
        return {
          key: review.record_id,
          primary: (
            <span className="flex flex-col gap-2">
              {motion !== null ? (
                <Link
                  to="/meetings/$meetingId/minutes"
                  params={{ meetingId: motion.meeting_id }}
                  className={LINK}
                >
                  {motion.held_on === null
                    ? (motion.meeting_title ?? '')
                    : rw.meetingAndDay(motion.meeting_title ?? '', displayDate(motion.held_on))}
                </Link>
              ) : consent !== null ? (
                <Link
                  to="/consents/$consentId"
                  params={{ consentId: consent.written_consent_id }}
                  className={LINK}
                >
                  {consent.title ?? rw.untitledConsent}
                </Link>
              ) : null}
              <span className={QUIET}>
                <Link
                  to="/form-990/returns/$recordId"
                  params={{ recordId: review.record_id }}
                  className={LINK}
                >
                  {rw.ofTheCopyProvidedOn(displayDate(review.copy_provided_on))}
                </Link>
              </span>
            </span>
          ),
          // CBG-FIX92: the tally on one line ("Carried 3–0"), never broken after its en dash.
          secondary:
            motion !== null ? (
              <>
                <span className="whitespace-nowrap">
                  {rw.tally(
                    motion.outcome,
                    motion.votes_for,
                    motion.votes_against,
                    motion.votes_abstaining,
                  )}
                </span>
                {motion.minutes_approved_on === null
                  ? null
                  : ` · ${rw.minutesApproved(displayDate(motion.minutes_approved_on))}`}
              </>
            ) : consent !== null ? (
              [
                consent.consent_date === null
                  ? null
                  : rw.consentDated(displayDate(consent.consent_date)),
                consentStatusLabel(consent.status),
              ]
                .filter((part): part is string => part !== null)
                .join(' · ')
            ) : undefined,
        };
      })}
    />
  );
}

/** Where a compensation decision is recorded, as a link to it: its meeting, its consent or its record. */
function DecisionPlace({ decision }: { decision: Form990CompensationDecision }) {
  if (decision.anchor_kind === 'motion' && decision.meeting_id !== null) {
    return (
      <Link
        to="/meetings/$meetingId/minutes"
        params={{ meetingId: decision.meeting_id }}
        className={LINK}
      >
        {rw.meetingAndDay(decision.meeting_title ?? '', displayDate(decision.decided_on))}
      </Link>
    );
  }
  if (decision.anchor_kind === 'written_consent' && decision.written_consent_id !== null) {
    return (
      <Link
        to="/consents/$consentId"
        params={{ consentId: decision.written_consent_id }}
        className={LINK}
      >
        {decision.consent_title ?? w.untitledConsent}
      </Link>
    );
  }
  if (decision.record_id !== null) {
    return (
      <Link to="/records/$recordId" params={{ recordId: decision.record_id }} className={LINK}>
        {decision.record_title ?? w.untitledRecord}
      </Link>
    );
  }
  return null;
}

/**
 * Lines 15a and 15b opened (the backend's `board-return-review.md` §20.6): each person the line's
 * decisions name, newest first, and under each its decisions — where it is recorded, the compensation
 * list's own timing sentence, and only what its record holds (documents named, a determination
 * recorded), never what it doesn't (the orchestrator's C21).
 */
function CompensationEvidence({
  decisions,
  testId,
}: {
  decisions: readonly Form990CompensationDecision[];
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-6" {...testIdProp(testId)}>
      <span className={HEADING}>{w.compensationHeading}</span>
      <ul className="flex flex-col">
        {compensationPeople(decisions).map((person) => {
          const newest = person.decisions[0];
          return (
            <li
              key={person.key}
              className="flex flex-col gap-8 border-t border-border-subtle py-8 first:border-t-0 first:pt-0"
              data-testid={`${testId}-person`}
            >
              <span className={STRONG}>
                {w.personAndPosition(newest?.person_name ?? '', newest?.position ?? '')}
              </span>
              <ul className="flex flex-col gap-10">
                {person.decisions.map((decision) => {
                  const facts = [
                    decision.documents_named !== null && decision.documents_named > 0
                      ? w.documentsNamed(decision.documents_named)
                      : null,
                    decision.determination_recorded === true ? w.determinationRecorded : null,
                  ]
                    .filter((part): part is string => part !== null)
                    // Each fact kept whole, so a phone wraps between them, never inside one.
                    .map(oneLine);
                  return (
                    <li
                      key={decision.decision_id}
                      className="flex flex-col gap-2"
                      data-testid={`${testId}-decision`}
                    >
                      <span className="font-sans text-s14">
                        <DecisionPlace decision={decision} />
                      </span>
                      {decision.timing_statement !== null ? (
                        <span className={PLAIN}>{decision.timing_statement}</span>
                      ) : null}
                      {facts.length > 0 ? (
                        <span className={QUIET} data-testid={`${testId}-facts`}>
                          {facts.join(' · ')}
                        </span>
                      ) : null}
                      {decision.placement === 'latest_before' ? (
                        <span className={QUIET}>{w.latestBeforeThisYear}</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
      <p className={QUIET}>{w.lineLabelNote}</p>
    </div>
  );
}

/**
 * What the board's records hold for this line, or `null` when the line's summary is a dash (the
 * caller then says "Board keeps no record that bears on this question.").
 */
export function LineEvidence({
  line,
  evidence,
  taxYear,
  quotations,
  scheduleO = null,
  testId,
}: {
  line: Form990Line;
  evidence: Form990Evidence;
  taxYear: Form990TaxYear;
  quotations: readonly Form990Quotation[];
  /** The Schedule O draft's address for the year shown (line 8b's no-committee note links it). */
  scheduleO?: Record<string, string | number> | null;
  testId: string;
}) {
  const kinds = line.evidence_kinds;
  if (kinds.includes('directors')) return <DirectorsEvidence evidence={evidence} testId={testId} />;
  if (kinds.includes('disclosure_cycles')) {
    return (
      <CyclesEvidence
        cycles={cyclesForLine(evidence.disclosure_cycles, line.line)}
        line={line.line}
        testId={testId}
      />
    );
  }
  if (kinds.includes('governing_document_versions')) {
    return (
      <VersionRows
        versions={evidence.governing_document_versions}
        testId={testId}
        heading={undefined}
      />
    );
  }
  if (kinds.includes('board_meetings'))
    return <MeetingsEvidence evidence={evidence} testId={testId} />;
  if (kinds.includes('committees')) {
    return (
      <CommitteesEvidence
        committees={evidence.committees}
        quotations={quotations}
        scheduleO={line.line === '8b' ? scheduleO : null}
        testId={testId}
      />
    );
  }
  const policy = POLICY_KINDS.find((kind) => kinds.includes(kind));
  if (policy !== undefined) {
    return <PolicyEvidence policy={evidence[policy]} taxYear={taxYear} testId={testId} />;
  }
  if (kinds.includes('conflicts')) return <ConflictsEvidence evidence={evidence} testId={testId} />;
  if (kinds.includes('return_copies') && evidence.return_copies !== null) {
    return <ReturnCopiesEvidence copies={evidence.return_copies} testId={testId} />;
  }
  if (kinds.includes('return_review') && evidence.return_review !== null) {
    return <ReturnReviewEvidence reviews={evidence.return_review} testId={testId} />;
  }
  if (kinds.includes('compensation_decisions')) {
    return (
      <CompensationEvidence
        decisions={compensationForLine(evidence.compensation_decisions, line.line)}
        testId={testId}
      />
    );
  }
  return null;
}

/**
 * Line 11b with no review linked, while the year has a copy: the way to record it, on the current
 * copy's receipts (the act lives there). Nothing for any other line.
 */
export function NothingRecordedWay({
  line,
  evidence,
  testId,
}: {
  line: Form990Line;
  evidence: Form990Evidence;
  testId: string;
}) {
  const current = evidence.return_copies?.[0];
  if (!line.evidence_kinds.includes('return_review') || current === undefined) return null;
  return (
    <Link
      to="/form-990/returns/$recordId"
      params={{ recordId: current.record_id }}
      className={`font-sans text-s14 ${LINK}`}
      data-testid={testId}
    >
      {rw.recordReview}
    </Link>
  );
}

/** Line 18's four boxes, as the form prints them, beneath the question when it is opened. */
export function Choices({ choices }: { choices: readonly string[] }) {
  if (choices.length === 0) return null;
  return <p className={QUIET}>{choices.join(' · ')}</p>;
}
